// ============================================
// document-toolbar.tsx — R14/R20 Document formatting toolbar
// ============================================
//
// Google-Docs-style formatting bar that consumes TipTap commands via
// the editor instance registered in `document-editor.tsx`.
//
// R20 layout (grouped for clarity — the user's R20 review said
// "maybe regroup in the toolbar so it's better for UX"):
//   Row 1: connection status + last-saved + peer count
//   Row 2 (grouped buttons, divided by vertical separators):
//     • Inline marks (bold / italic / underline)
//     • Block style (H1 / H2 / H3 / ¶ / • / 1. / “)
//     • Alignment (left / center / right / justify)
//     • Text style (font family dropdown, font-size Select +
//       custom-size input, colour swatches + native picker)
//     • Insert (table dropdown, table-of-contents button)
//     • History (undo / redo) — right side
//     • Export (PDF / DOCX) — far right
//
// Active formatting is highlighted by listening to the editor's
// `selectionUpdate` event so the buttons stay in sync as the cursor
// moves.

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Editor } from '@tiptap/react';
import {
    Eye,
    Bold,
    Italic,
    Underline as UnderlineIcon,
    Heading1,
    Heading2,
    Heading3,
    List,
    ListOrdered,
    Quote,
    Pilcrow,
    Table as TableIcon,
    Undo2,
    Redo2,
    Type as TypeIcon,
    Wifi,
    WifiOff,
    Users,
    Download,
    AlignLeft,
    AlignCenter,
    AlignRight,
    AlignJustify,
    ListTree,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';

import { useDocumentI18n } from './document-i18n';
import type { DocumentSaveStatus } from './document-view';
import { cn } from '@/lib/utils';
import {
    getDocumentEditor,
} from './document-editor';
import {
    downloadBlob,
    exportDocumentAsDocx,
    exportDocumentAsPdf,
    sanitiseFilename,
} from './document-export';
import { useToast } from '@/hooks/use-toast';

// ============================================
// TYPES
// ============================================

export interface DocumentToolbarProps {
    readOnly?: boolean;
    /** Yjs room name — used to look up the live editor instance. */
    roomName: string;
    /** WS status — drives the live/offline indicator. */
    connectionStatus: 'connecting' | 'connected' | 'disconnected';
    /** Other peers currently editing this document. */
    peers: Array<{ name: string; color: string }>;
    /** Last-saved ISO timestamp, if any. */
    lastSavedAt: string | null;
    /**
     * R26 — honest auto-save state owned by `DocumentView`:
     *
     *   - `saving` → "Enregistrement…" (a POST is in flight);
     *   - `dirty`  → "Modifications non enregistrées" (bytes pending, the
     *                idle/max-wait deadline is still counting);
     *   - `error`  → "Échec de l'enregistrement" (last POST failed, the
     *                edits are kept dirty and retried);
     *   - `saved`  → "Enregistré" / "saved X ago".
     *
     * Replaces the old `isSaving: boolean`, which rendered "Enregistré"
     * whenever no request was in flight — even while thousands of bytes
     * were still unsaved, or after a failed upload.
     */
    saveStatus: DocumentSaveStatus;
    /**
     * R15: last WS error message. Surfaced in the status row so
     * the user can see WHY the connection is offline (mixed
     * content, server crash, DNS, etc.). Omitted in the success
     * path.
     */
    connectionError?: string | null;
    // R18 (t59): export download support. The toolbar needs the
    // current view name + task list + member list so the export
    // helpers can resolve @-mentions into task titles + person
    // names.
    /** Display name of the active view (used as the download filename). */
    viewName: string;
    /** Project tasks — used to resolve @-task mentions during export. */
    mentionTasks: ReadonlyArray<{ id: string; title: string }>;
    /** Project members — used to resolve @-person mentions during export. */
    mentionMembers: ReadonlyArray<{ user: { id: string; name: string } }>;
}

// ============================================
// ACTIVE-FORMATTING HOOK
// ============================================
//
// TipTap fires `selectionUpdate` + `transaction` whenever the cursor
// moves or the doc mutates. We subscribe to both and mirror a few
// booleans into local state so the toolbar buttons can highlight
// themselves. We don't re-render the toolbar for every keystroke —
// only when the active marks actually change.

interface ActiveFormat {
    bold: boolean;
    italic: boolean;
    underline: boolean;
    h1: boolean;
    h2: boolean;
    h3: boolean;
    paragraph: boolean;
    bulletList: boolean;
    orderedList: boolean;
    blockquote: boolean;
    // R20: text-alignment highlights for the current paragraph.
    alignLeft: boolean;
    alignCenter: boolean;
    alignRight: boolean;
    alignJustify: boolean;
    // R20: track the current font-family / font-size / color so the
    // Select + colour swatches can highlight the active value. We
    // read these off the current selection's `textStyle` mark
    // attributes.
    fontFamily: string | null;
    fontSize: string | null;
    textColor: string | null;
}

const INITIAL_FORMAT: ActiveFormat = {
    bold: false,
    italic: false,
    underline: false,
    h1: false,
    h2: false,
    h3: false,
    paragraph: false,
    bulletList: false,
    orderedList: false,
    blockquote: false,
    alignLeft: false,
    alignCenter: false,
    alignRight: false,
    alignJustify: false,
    fontFamily: null,
    fontSize: null,
    textColor: null,
};

function readActiveFormat(editor: Editor): ActiveFormat {
    // R20: read the current paragraph's `textAlign` attribute. We
    // pull the value via TipTap's `getAttributes` API and only set
    // the matching boolean to true. The defaults (left) is the
    // fallback when no explicit alignment has been applied.
    const align = (editor.getAttributes('paragraph')?.textAlign ?? null) as
        | 'left'
        | 'center'
        | 'right'
        | 'justify'
        | null;
    // Read font / size / colour off the textStyle mark attributes.
    // TipTap exposes `editor.getAttributes('textStyle')` which returns
    // the active mark's attrs for the current selection (or {} if
    // none).
    const textStyleAttrs = (editor.getAttributes('textStyle') ?? {}) as {
        fontFamily?: string | null;
        fontSize?: string | null;
        color?: string | null;
    };
    return {
        bold: editor.isActive('bold'),
        italic: editor.isActive('italic'),
        underline: editor.isActive('underline'),
        h1: editor.isActive('heading', { level: 1 }),
        h2: editor.isActive('heading', { level: 2 }),
        h3: editor.isActive('heading', { level: 3 }),
        paragraph: editor.isActive('paragraph'),
        bulletList: editor.isActive('bulletList'),
        orderedList: editor.isActive('orderedList'),
        blockquote: editor.isActive('blockquote'),
        alignLeft: align === 'left',
        alignCenter: align === 'center',
        alignRight: align === 'right',
        alignJustify: align === 'justify',
        fontFamily: textStyleAttrs.fontFamily ?? null,
        fontSize: textStyleAttrs.fontSize ?? null,
        textColor: textStyleAttrs.color ?? null,
    };
}

// ============================================
// COMPONENT
// ============================================

export function DocumentToolbar({
    roomName,
    readOnly = false,
    connectionStatus,
    peers,
    lastSavedAt,
    saveStatus,
    connectionError,
    viewName,
    mentionTasks,
    mentionMembers,
}: DocumentToolbarProps) {
    const { t, language, formatLastSavedAt, formatPeerCount } = useDocumentI18n();
    const { toast } = useToast();
    const [format, setFormat] = useState<ActiveFormat>(INITIAL_FORMAT);
    const [exportingAs, setExportingAs] = useState<'pdf' | 'docx' | null>(null);

    // Look up the live editor each render. The editor registry is a
    // tiny imperative bridge (see document-editor.tsx) so the toolbar
    // can stay outside the editor's React tree.
    const editor = getDocumentEditor(roomName);

    // R21: the toolbar's ListTree button inserts a live document TOC at the
    // cursor. The R20 popover (menuEntries, handlePick, DocumentTocMenu) was
    // navigation only and is gone; the node lives in document-toc.ts.
    const insertToc = useCallback(() => {
        if (!editor || !editor.isEditable) return;
        editor.chain().focus().insertContent({ type: 'documentToc' }).run();
    }, [editor]);

    // R18 (t59): pre-compute the export filename once per viewName
    // change so the dropdown label shows it and the helper gets a
    // sanitised stem.
    const exportFileName = useMemo(
        () => sanitiseFilename(viewName, 'document'),
        [viewName]
    );

    const handleExportPdf = useCallback(async () => {
        if (!editor || exportingAs) return;
        setExportingAs('pdf');
        try {
            // R19 (t60) — bug 5 fix: wait for the editor's content
            // to actually be ready before exporting. Without this
            // the export can race the Yjs hydration + WS sync and
            // return an empty document because the editor's
            // ProseMirror state still has the placeholder
            // paragraph. `editor.state.doc` is a stable
            // ProseMirror Node that's only populated after the
            // first hydration tick.
            if (!editor.state || editor.isDestroyed) {
                toast({
                    title: 'Editor not ready',
                    description: 'Please wait a moment and try again.',
                    variant: 'destructive',
                });
                return;
            }
            const blob = await exportDocumentAsPdf(editor, {
                fileName: exportFileName,
                language,
                // The hooks return Task[] / ProjectMember[] — we
                // narrow here so the export helper stays typed.
                tasks: mentionTasks as unknown as import('@/lib/schemas').Task[],
                members: mentionMembers as unknown as import('@/lib/schemas').ProjectMember[],
            });
            // R19 (t60) — bug 5 fix: guard against the "empty PDF"
            // symptom by skipping the download when the blob is
            // either empty or smaller than ~1 KB (a real PDF
            // always weighs a few KB due to the PDF header).
            if (!blob || blob.size < 1024) {
                console.warn(
                    '[document-export R20] PDF blob too small, skipping download',
                    blob?.size ?? 0,
                    'bytes'
                );
                toast({
                    title: 'Nothing to export',
                    description: 'The document appears to be empty.',
                    variant: 'destructive',
                });
                return;
            }
            downloadBlob(blob, exportFileName, 'pdf');
        } catch (err) {
            console.error('[document-export] PDF failed', err);
            toast({
                title: 'PDF export failed',
                description: err instanceof Error ? err.message : 'Unknown error',
                variant: 'destructive',
            });
        } finally {
            setExportingAs(null);
        }
    }, [editor, exportFileName, exportingAs, mentionTasks, mentionMembers, toast, language]);

    const handleExportDocx = useCallback(async () => {
        if (!editor || exportingAs) return;
        setExportingAs('docx');
        try {
            const blob = await exportDocumentAsDocx(editor, {
                fileName: exportFileName,
                language,
                tasks: mentionTasks as unknown as import('@/lib/schemas').Task[],
                members: mentionMembers as unknown as import('@/lib/schemas').ProjectMember[],
            });
            downloadBlob(blob, exportFileName, 'docx');
        } catch (err) {
            console.error('[document-export] DOCX failed', err);
            toast({
                title: 'DOCX export failed',
                description: err instanceof Error ? err.message : 'Unknown error',
                variant: 'destructive',
            });
        } finally {
            setExportingAs(null);
        }
    }, [editor, exportFileName, exportingAs, mentionTasks, mentionMembers, toast, language]);

    useEffect(() => {
        if (!editor) {
            setFormat(INITIAL_FORMAT);
            return;
        }
        const handler = () => setFormat(readActiveFormat(editor));
        editor.on('selectionUpdate', handler);
        editor.on('transaction', handler);
        handler();
        return () => {
            editor.off('selectionUpdate', handler);
            editor.off('transaction', handler);
        };
    }, [editor]);

    const statusLabel = useMemo(() => {
        if (readOnly) return t('document.readOnlyBadge');
        if (connectionStatus === 'connected') return t('document.connected');
        if (connectionStatus === 'connecting') return t('document.connecting');
        return t('document.disconnected');
    }, [connectionStatus, readOnly, t]);

    const saveLabel = useMemo(() => {
        if (saveStatus === 'saving') return t('document.saveStatus.saving');
        if (saveStatus === 'dirty') return t('document.saveStatus.dirty');
        if (saveStatus === 'error') return t('document.saveStatus.error');
        if (lastSavedAt) return formatLastSavedAt(lastSavedAt);
        return t('document.saveStatus.saved');
    }, [saveStatus, lastSavedAt, formatLastSavedAt, t]);

    return (
        <div
            className="flex flex-col gap-2 rounded-md border border-border bg-surface/60 p-2"
            data-testid="document-toolbar"
        >
            {/* Status row: connection + last-saved + peer count */}
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-foreground-muted">
                <div className="flex flex-wrap items-center gap-3">
                    <span
                        className={cn(
                            'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5',
                            readOnly ? 'bg-primary/10 text-primary' : connectionStatus === 'connected'
                                ? 'bg-emerald-500/15 text-emerald-300'
                                : connectionStatus === 'connecting'
                                    ? 'bg-amber-500/15 text-amber-300'
                                    : 'bg-red-500/15 text-red-300'
                        )}
                        data-testid="document-connection-status"
                        data-status={connectionStatus}
                        title={
                            connectionError
                                ? `${statusLabel}: ${connectionError}`
                                : statusLabel
                        }
                    >
                        {readOnly ? <Eye className="h-3 w-3" /> : connectionStatus === 'disconnected' ? (
                            <WifiOff className="h-3 w-3" />
                        ) : (
                            <Wifi className="h-3 w-3" />
                        )}
                        {statusLabel}
                    </span>
                    {/* R15: surface the last WS error so the user can
                        see WHY the connection is offline (mixed
                        content, server crash, DNS, etc.). Rendered
                        inline next to the status badge to keep the
                        toolbar compact. Truncated to avoid wrapping
                        the entire toolbar on mobile. */}
                    {connectionError && connectionStatus !== 'connected' ? (
                        <span
                            className="max-w-[260px] truncate text-red-300/90"
                            data-testid="document-connection-error"
                            title={connectionError}
                        >
                            {connectionError}
                        </span>
                    ) : null}
                    {/* R26: the honest save state is also exposed as
                        `data-status` so the behaviour is assertable
                        (saved | dirty | saving | error). */}
                    <span
                        data-testid="document-save-status"
                        data-status={saveStatus}
                        className={cn(
                            saveStatus === 'error' && 'text-red-300',
                            saveStatus === 'saving' && 'text-amber-300'
                        )}
                    >
                        {saveLabel}
                    </span>
                </div>
                <div
                    className="flex items-center gap-2"
                    data-testid="document-peer-count"
                    data-count={peers.length}
                >
                    <Users className="h-3.5 w-3.5" />
                    <span>{formatPeerCount(peers.length)}</span>
                    {peers.length > 0 && (
                        <span className="flex -space-x-1">
                            {peers.slice(0, 5).map((p) => (
                                <span
                                    key={p.name + p.color}
                                    className="inline-block h-3 w-3 rounded-full border border-surface"
                                    style={{ backgroundColor: p.color }}
                                    title={p.name}
                                />
                            ))}
                        </span>
                    )}
                </div>
            </div>

            {/* Formatting row */}
            <div className="flex flex-wrap items-center gap-0.5 sm:gap-1">
                <Button
                    type="button"
                    variant={format.bold ? 'secondary' : 'ghost'}
                    size="icon" className="h-11 w-11 sm:h-10 sm:w-10"
                    disabled={!editor || readOnly}
                    onClick={() => editor?.chain().focus().toggleBold().run()}
                    title={t('document.toolbar.bold')}
                    data-testid="document-toolbar-bold"
                    aria-pressed={format.bold}
                >
                    <Bold className="h-4 w-4" />
                </Button>
                <Button
                    type="button"
                    variant={format.italic ? 'secondary' : 'ghost'}
                    size="icon" className="h-11 w-11 sm:h-10 sm:w-10"
                    disabled={!editor || readOnly}
                    onClick={() => editor?.chain().focus().toggleItalic().run()}
                    title={t('document.toolbar.italic')}
                    data-testid="document-toolbar-italic"
                    aria-pressed={format.italic}
                >
                    <Italic className="h-4 w-4" />
                </Button>
                <Button
                    type="button"
                    variant={format.underline ? 'secondary' : 'ghost'}
                    size="icon" className="h-11 w-11 sm:h-10 sm:w-10"
                    disabled={!editor || readOnly}
                    onClick={() => editor?.chain().focus().toggleUnderline().run()}
                    title={t('document.toolbar.underline')}
                    data-testid="document-toolbar-underline"
                    aria-pressed={format.underline}
                >
                    <UnderlineIcon className="h-4 w-4" />
                </Button>

                <span className="mx-1 h-5 w-px bg-white/10" aria-hidden />

                <Button
                    type="button"
                    variant={format.h1 ? 'secondary' : 'ghost'}
                    size="icon" className="h-11 w-11 sm:h-10 sm:w-10"
                    disabled={!editor || readOnly}
                    onClick={() => editor?.chain().focus().toggleHeading({ level: 1 }).run()}
                    title={t('document.toolbar.h1')}
                    data-testid="document-toolbar-h1"
                    aria-pressed={format.h1}
                >
                    <Heading1 className="h-4 w-4" />
                </Button>
                <Button
                    type="button"
                    variant={format.h2 ? 'secondary' : 'ghost'}
                    size="icon" className="h-11 w-11 sm:h-10 sm:w-10"
                    disabled={!editor || readOnly}
                    onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
                    title={t('document.toolbar.h2')}
                    data-testid="document-toolbar-h2"
                    aria-pressed={format.h2}
                >
                    <Heading2 className="h-4 w-4" />
                </Button>
                <Button
                    type="button"
                    variant={format.h3 ? 'secondary' : 'ghost'}
                    size="icon" className="h-11 w-11 sm:h-10 sm:w-10"
                    disabled={!editor || readOnly}
                    onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()}
                    title={t('document.toolbar.h3')}
                    data-testid="document-toolbar-h3"
                    aria-pressed={format.h3}
                >
                    <Heading3 className="h-4 w-4" />
                </Button>

                <span className="mx-1 h-5 w-px bg-white/10" aria-hidden />

                <Button
                    type="button"
                    variant={format.paragraph ? 'secondary' : 'ghost'}
                    size="icon" className="h-11 w-11 sm:h-10 sm:w-10"
                    disabled={!editor || readOnly}
                    onClick={() => editor?.chain().focus().setParagraph().run()}
                    title={t('document.toolbar.paragraph')}
                    data-testid="document-toolbar-paragraph"
                    aria-pressed={format.paragraph}
                >
                    <Pilcrow className="h-4 w-4" />
                </Button>
                <Button
                    type="button"
                    variant={format.bulletList ? 'secondary' : 'ghost'}
                    size="icon" className="h-11 w-11 sm:h-10 sm:w-10"
                    disabled={!editor || readOnly}
                    onClick={() => editor?.chain().focus().toggleBulletList().run()}
                    title={t('document.toolbar.bulletList')}
                    data-testid="document-toolbar-bullet-list"
                    aria-pressed={format.bulletList}
                >
                    <List className="h-4 w-4" />
                </Button>
                <Button
                    type="button"
                    variant={format.orderedList ? 'secondary' : 'ghost'}
                    size="icon" className="h-11 w-11 sm:h-10 sm:w-10"
                    disabled={!editor || readOnly}
                    onClick={() => editor?.chain().focus().toggleOrderedList().run()}
                    title={t('document.toolbar.orderedList')}
                    data-testid="document-toolbar-ordered-list"
                    aria-pressed={format.orderedList}
                >
                    <ListOrdered className="h-4 w-4" />
                </Button>
                <Button
                    type="button"
                    variant={format.blockquote ? 'secondary' : 'ghost'}
                    size="icon" className="h-11 w-11 sm:h-10 sm:w-10"
                    disabled={!editor || readOnly}
                    onClick={() => editor?.chain().focus().toggleBlockquote().run()}
                    title={t('document.toolbar.blockquote')}
                    data-testid="document-toolbar-blockquote"
                    aria-pressed={format.blockquote}
                >
                    <Quote className="h-4 w-4" />
                </Button>

                <span className="mx-1 h-5 w-px bg-white/10" aria-hidden />

                {/* R20: text alignment (left / center / right / justify).
                    The `format.align*` booleans come from
                    `readActiveFormat` and reflect the current paragraph's
                    `textAlign` attribute. We don't store the value in
                    React state — TipTap's editor selectionUpdate /
                    transaction events drive the highlight so it stays
                    in sync with the cursor's paragraph. */}
                <Button
                    type="button"
                    variant={format.alignLeft ? 'secondary' : 'ghost'}
                    size="icon" className="h-11 w-11 sm:h-10 sm:w-10"
                    disabled={!editor || readOnly}
                    onClick={() => editor?.chain().focus().setTextAlign('left').run()}
                    title={t('document.toolbar.alignLeftHint')}
                    data-testid="document-toolbar-align-left"
                    aria-label={t('document.toolbar.alignLeft')}
                    aria-pressed={format.alignLeft}
                >
                    <AlignLeft className="h-4 w-4" />
                </Button>
                <Button
                    type="button"
                    variant={format.alignCenter ? 'secondary' : 'ghost'}
                    size="icon" className="h-11 w-11 sm:h-10 sm:w-10"
                    disabled={!editor || readOnly}
                    onClick={() => editor?.chain().focus().setTextAlign('center').run()}
                    title={t('document.toolbar.alignCenterHint')}
                    data-testid="document-toolbar-align-center"
                    aria-label={t('document.toolbar.alignCenter')}
                    aria-pressed={format.alignCenter}
                >
                    <AlignCenter className="h-4 w-4" />
                </Button>
                <Button
                    type="button"
                    variant={format.alignRight ? 'secondary' : 'ghost'}
                    size="icon" className="h-11 w-11 sm:h-10 sm:w-10"
                    disabled={!editor || readOnly}
                    onClick={() => editor?.chain().focus().setTextAlign('right').run()}
                    title={t('document.toolbar.alignRightHint')}
                    data-testid="document-toolbar-align-right"
                    aria-label={t('document.toolbar.alignRight')}
                    aria-pressed={format.alignRight}
                >
                    <AlignRight className="h-4 w-4" />
                </Button>
                <Button
                    type="button"
                    variant={format.alignJustify ? 'secondary' : 'ghost'}
                    size="icon" className="h-11 w-11 sm:h-10 sm:w-10"
                    disabled={!editor || readOnly}
                    onClick={() => editor?.chain().focus().setTextAlign('justify').run()}
                    title={t('document.toolbar.alignJustifyHint')}
                    data-testid="document-toolbar-align-justify"
                    aria-label={t('document.toolbar.alignJustify')}
                    aria-pressed={format.alignJustify}
                >
                    <AlignJustify className="h-4 w-4" />
                </Button>

                <span className="mx-1 h-5 w-px bg-white/10" aria-hidden />

                {/* ============================================
                    Text style group: font family, font size,
                    custom-size input, text colour palette +
                    native picker. The previous version only had a
                    3-option font dropdown + 3-option size Select;
                    R20 adds 5 more fonts, a custom-size numeric
                    input, and a colour palette with 8 presets +
                    a native <input type="color"> for arbitrary
                    hex codes.
                    ============================================ */}

                {/* Font family — 8 options */}
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            disabled={!editor || readOnly}
                            data-testid="document-toolbar-font-family"
                            className="max-w-[120px]"
                        >
                            <TypeIcon className="mr-1 h-4 w-4 shrink-0" />
                            <span className="truncate">
                                {format.fontFamily
                                    ? format.fontFamily.split(',')[0]?.replace(/['"]/g, '') ?? t('document.toolbar.fontFamily')
                                    : t('document.toolbar.fontFamily')}
                            </span>
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                        <DropdownMenuLabel>{t('document.toolbar.fontFamily')}</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                            onSelect={() => editor?.chain().focus().setFontFamily('Inter, ui-sans-serif, system-ui, sans-serif').run()}
                            data-testid="document-toolbar-font-sans"
                        >
                            {t('document.toolbar.fontFamily.sans')}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            onSelect={() => editor?.chain().focus().setFontFamily('Arial, sans-serif').run()}
                            data-testid="document-toolbar-font-arial"
                        >
                            {t('document.toolbar.fontFamily.arial')}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            onSelect={() => editor?.chain().focus().setFontFamily('Georgia, ui-serif, serif').run()}
                            data-testid="document-toolbar-font-serif"
                        >
                            {t('document.toolbar.fontFamily.serif')}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            onSelect={() => editor?.chain().focus().setFontFamily('"Times New Roman", Times, serif').run()}
                            data-testid="document-toolbar-font-times"
                        >
                            {t('document.toolbar.fontFamily.times')}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            onSelect={() => editor?.chain().focus().setFontFamily('Verdana, Geneva, sans-serif').run()}
                            data-testid="document-toolbar-font-verdana"
                        >
                            {t('document.toolbar.fontFamily.verdana')}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            onSelect={() => editor?.chain().focus().setFontFamily('"Courier New", Courier, monospace').run()}
                            data-testid="document-toolbar-font-courier"
                        >
                            {t('document.toolbar.fontFamily.courier')}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            onSelect={() => editor?.chain().focus().setFontFamily('ui-monospace, SFMono-Regular, Menlo, monospace').run()}
                            data-testid="document-toolbar-font-mono"
                        >
                            {t('document.toolbar.fontFamily.mono')}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            onSelect={() => editor?.chain().focus().setFontFamily('"Comic Sans MS", cursive, sans-serif').run()}
                            data-testid="document-toolbar-font-comic"
                        >
                            {t('document.toolbar.fontFamily.comic')}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                            onSelect={() => {
                                if (!editor) return;
                                // R20: clearing the font requires
                                // setting the attribute to null, NOT
                                // calling setFontFamily(null) (which
                                // is typed as a string). We mirror
                                // the FontFamily extension's
                                // `unsetFontFamily` flow here:
                                // setMark(textStyle, { fontFamily: null })
                                // + removeEmptyTextStyle.
                                editor
                                    .chain()
                                    .focus()
                                    .setMark('textStyle', { fontFamily: null })
                                    .run();
                            }}
                            data-testid="document-toolbar-font-default"
                        >
                            {t('document.toolbar.fontFamily.default')}
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>

                {/* Custom font-size input (R20 round 3): the user
                    explicitly asked for a single default size (12px)
                    plus a custom-px input. We removed the size-SELECT
                    presets. The default is 12px (set on the
                    .doc-editor-content container) so the input shows
                    `12` when the selection has no explicit size.
                    Selecting text + typing in the input applies the
                    size immediately. */}
                <Input
                    type="number"
                    inputMode="numeric"
                    min={6}
                    max={200}
                    step={1}
                    placeholder="12"
                    className="h-8 w-[72px] text-xs"
                    disabled={!editor || readOnly}
                    data-testid="document-toolbar-font-size-custom"
                    defaultValue={format.fontSize ? String(parsePx(format.fontSize)) : '12'}
                    key={format.fontSize ?? 'default'}
                    onChange={(e) => {
                        const raw = e.target.value;
                        const px = parseInt(raw, 10);
                        if (!editor) return;
                        if (raw === '') {
                            // Empty input → set 12px so the selected
                            // text matches the document default (we
                            // don't strip the inline style because
                            // that would revert to the browser's
                            // 16px default).
                            editor.chain().focus().setFontSize('12px').run();
                            return;
                        }
                        if (!Number.isFinite(px) || px < 6 || px > 200) return;
                        editor.chain().focus().setFontSize(`${px}px`).run();
                    }}
                    title={t('document.toolbar.fontSizeCustomHint')}
                />

                {/* Text colour — palette + native picker. */}
                <DocumentTextColorPicker
                    currentColor={format.textColor}
                    disabled={!editor || readOnly}
                    onPick={(color) => {
                        if (!editor) return;
                        editor.chain().focus().setColor(color).run();
                    }}
                />

                <span className="mx-1 h-5 w-px bg-white/10" aria-hidden />

                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon" className="h-11 w-11 sm:h-10 sm:w-10"
                            disabled={!editor || readOnly}
                            title={t('document.toolbar.insertTable')}
                            data-testid="document-toolbar-table-menu"
                        >
                            <TableIcon className="h-4 w-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                        <DropdownMenuItem
                            onSelect={() =>
                                editor?.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
                            }
                            data-testid="document-toolbar-insert-table"
                        >
                            {t('document.toolbar.insertTable')}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            onSelect={() => editor?.chain().focus().addRowAfter().run()}
                            data-testid="document-toolbar-add-row"
                        >
                            {t('document.toolbar.addRowAfter')}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            onSelect={() => editor?.chain().focus().addColumnAfter().run()}
                            data-testid="document-toolbar-add-column"
                        >
                            {t('document.toolbar.addColumnAfter')}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            onSelect={() => editor?.chain().focus().deleteRow().run()}
                            data-testid="document-toolbar-delete-row"
                        >
                            {t('document.toolbar.deleteRow')}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            onSelect={() => editor?.chain().focus().deleteColumn().run()}
                            data-testid="document-toolbar-delete-column"
                        >
                            {t('document.toolbar.deleteColumn')}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            onSelect={() => editor?.chain().focus().deleteTable().run()}
                            data-testid="document-toolbar-delete-table"
                        >
                            {t('document.toolbar.deleteTable')}
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>

                <span className="mx-1 h-5 w-px bg-white/10" aria-hidden />

                <Button
                    type="button"
                    variant="ghost"
                    size="icon" className="h-11 w-11 sm:h-10 sm:w-10"
                    disabled={!editor || readOnly}
                    onClick={() => editor?.chain().focus().undo().run()}
                    title={t('document.toolbar.undo')}
                    data-testid="document-toolbar-undo"
                >
                    <Undo2 className="h-4 w-4" />
                </Button>
                <Button
                    type="button"
                    variant="ghost"
                    size="icon" className="h-11 w-11 sm:h-10 sm:w-10"
                    disabled={!editor || readOnly}
                    onClick={() => editor?.chain().focus().redo().run()}
                    title={t('document.toolbar.redo')}
                    data-testid="document-toolbar-redo"
                >
                    <Redo2 className="h-4 w-4" />
                </Button>

                <span className="mx-1 h-5 w-px bg-white/10" aria-hidden />

                {/* R21 — Table of Contents: inserts a live TOC node at the
                    cursor. Entries follow headings (custom labels keep a
                    stable id link); title/empty strings localise. */}
                <Button
                    type="button"
                    variant="ghost"
                    size="icon" className="h-11 w-11 sm:h-10 sm:w-10"
                    disabled={!editor || !editor.isEditable}
                    onClick={insertToc}
                    title={t('document.toolbar.tocTitle')}
                    data-testid="document-toolbar-toc"
                    aria-label={t('document.toolbar.tocTitle')}
                >
                    <ListTree className="h-4 w-4" />
                </Button>

                <span className="mx-1 h-5 w-px bg-white/10" aria-hidden />

                {/* R18 (t59): download dropdown — exports the
                    current view to PDF or DOCX. Sits at the right
                    edge of the formatting row so it reads as the
                    "primary destination" of the toolbar. */}
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-11 w-11 sm:h-10 sm:w-10"
                            disabled={!editor || exportingAs !== null}
                            title={t('document.exportTitle')}
                            data-testid="document-toolbar-export"
                        >
                            <Download className="h-4 w-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                        align="end"
                        data-testid="document-toolbar-export-menu"
                    >
                        <DropdownMenuItem
                            onSelect={handleExportPdf}
                            disabled={exportingAs !== null}
                            data-testid="document-toolbar-export-pdf"
                        >
                            {exportingAs === 'pdf' ? '…' : t('document.exportPdf')}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            onSelect={handleExportDocx}
                            disabled={exportingAs !== null}
                            data-testid="document-toolbar-export-docx"
                        >
                            {exportingAs === 'docx' ? '…' : t('document.exportDocx')}
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </div>
    );
}

// ============================================
// HELPERS
// ============================================

/**
 * Parse a font-size string (e.g. "12px", "1rem", "0.875rem") and
 * return the numeric value in pixels. We resolve `rem` against
 * the document's root font-size (16px by browser default).
 * Returns 0 when the input is null / unparseable — the input
 * falls back to its `placeholder` in that case.
 */
function parsePx(size: string | null): number {
    if (!size) return 12;
    const trimmed = size.trim();
    const remMatch = trimmed.match(/^([\d.]+)\s*rem$/);
    if (remMatch) {
        return Math.round(parseFloat(remMatch[1] ?? '0') * 16);
    }
    const pxMatch = trimmed.match(/^([\d.]+)\s*px$/);
    if (pxMatch) {
        return parseInt(pxMatch[1] ?? '0', 10);
    }
    const num = parseFloat(trimmed);
    return Number.isFinite(num) ? Math.round(num) : 12;
}

// ============================================
// DOCUMENT TEXT COLOUR PICKER (R20)
// ============================================
//
// Compact swatch palette + native <input type="color"> for custom
// colours. The 8 preset colours are hand-picked to cover the
// common cases; the native picker handles everything else. We
// render an inverted "A" icon on each swatch so the colour is
// visible even when the user picks white / very-light colours.

const PRESET_COLORS: ReadonlyArray<{ label: string; value: string }> = [
    { label: 'Black', value: '#000000' },
    { label: 'Gray', value: '#6b7280' },
    { label: 'Red', value: '#ef4444' },
    { label: 'Orange', value: '#f97316' },
    { label: 'Yellow', value: '#eab308' },
    { label: 'Green', value: '#22c55e' },
    { label: 'Blue', value: '#3b82f6' },
    { label: 'Purple', value: '#a855f7' },
];

interface DocumentTextColorPickerProps {
    /** Currently-applied colour (hex / rgb / null). */
    currentColor: string | null;
    /** When true the trigger + items are disabled. */
    disabled?: boolean;
    /** Called with the new colour (null = clear). */
    onPick: (color: string | null) => void;
}

function DocumentTextColorPicker({
    currentColor,
    disabled,
    onPick,
}: DocumentTextColorPickerProps) {
    const { t } = useDocumentI18n();
    return (
        <Popover>
            <PopoverTrigger asChild>
                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-11 w-11 sm:h-10 sm:w-10"
                    disabled={disabled}
                    title={t('document.toolbar.color')}
                    data-testid="document-toolbar-color"
                    aria-label={t('document.toolbar.color')}
                >
                    <span className="relative inline-block">
                        <span className="font-bold text-base leading-none">A</span>
                        <span
                            className="absolute -bottom-1 left-0 h-[3px] w-full rounded"
                            style={{ backgroundColor: currentColor ?? 'currentColor' }}
                        />
                    </span>
                </Button>
            </PopoverTrigger>
            <PopoverContent
                align="start"
                className="w-[220px]"
                data-testid="document-toolbar-color-menu"
            >
                <div className="grid grid-cols-4 gap-1.5">
                    {PRESET_COLORS.map((c) => (
                        <button
                            key={c.value}
                            type="button"
                            onClick={() => onPick(c.value)}
                            title={c.label}
                            data-testid={`document-toolbar-color-${c.label.toLowerCase()}`}
                            data-color={c.value}
                            className="group flex h-8 w-full items-center justify-center rounded border border-border bg-surface transition-colors hover:bg-surface-hover"
                        >
                            <span
                                className="block h-4 w-4 rounded-sm"
                                style={{ backgroundColor: c.value }}
                            />
                        </button>
                    ))}
                </div>
                <div className="mt-3 flex items-center gap-2 border-t border-border pt-2">
                    <label
                        className="text-xs text-foreground-muted"
                        htmlFor="document-toolbar-color-custom"
                    >
                        {t('document.toolbar.colorCustom')}
                    </label>
                    <input
                        id="document-toolbar-color-custom"
                        type="color"
                        defaultValue={currentColor ?? '#000000'}
                        disabled={disabled}
                        className="h-7 w-12 cursor-pointer rounded border border-border bg-transparent"
                        data-testid="document-toolbar-color-picker"
                        onChange={(e) => onPick(e.target.value)}
                    />
                    <button
                        type="button"
                        onClick={() => onPick(null)}
                        disabled={disabled}
                        className="ml-auto text-xs text-foreground-muted underline-offset-2 hover:underline"
                        data-testid="document-toolbar-color-clear"
                    >
                        {t('document.toolbar.colorClear')}
                    </button>
                </div>
            </PopoverContent>
        </Popover>
    );
}
